import React, { useState } from "react";
import "../App.css";
import logo from "./IPTalonsLogo.png";

const LabLockSignupPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    institution: "",
    email: "",
    orcid: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [noOrcid, setNoOrcid] = useState(false);
  const [showWorksSelection, setShowWorksSelection] = useState(false);
  const [authorMatches, setAuthorMatches] = useState([]);
  const [selectedWorks, setSelectedWorks] = useState([]);
  const [worksLoading, setWorksLoading] = useState(false);

  const validateOrcid = (orcid) => {
    const cleanOrcid = orcid.replace(/[-\s]/g, '');
    const orcidPattern = /^\d{16}$/;
    return orcidPattern.test(cleanOrcid);
  };

  const formatOrcid = (orcid) => {
    const cleanOrcid = orcid.replace(/[-\s]/g, '');
    if (cleanOrcid.length === 16) {
      return `${cleanOrcid.slice(0, 4)}-${cleanOrcid.slice(4, 8)}-${cleanOrcid.slice(8, 12)}-${cleanOrcid.slice(12, 16)}`;
    }
    return orcid;
  };

  const extractOrcidFromUrl = (input) => {
    const orcidUrlPattern = /(?:https?:\/\/)?(?:www\.)?orcid\.org\/(\d{4}-\d{4}-\d{4}-\d{4})/;
    const match = input.match(orcidUrlPattern);
    return match ? match[1] : input;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (error) setError("");
  };

  const handleOrcidChange = (e) => {
    let value = e.target.value;
    value = extractOrcidFromUrl(value);
    setFormData(prev => ({
      ...prev,
      orcid: value
    }));
    
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate required fields
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.institution.trim() || !formData.email.trim()) {
      setError("Please fill in all required fields.");
      setLoading(false);
      return;
    }

    // Validate ORCID only if not in "no ORCID" mode
    if (!noOrcid && !formData.orcid.trim()) {
      setError("Please provide an ORCID ID or select 'I don't have an ORCID ID'.");
      setLoading(false);
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    // If ORCID provided, validate and use existing flow
    if (!noOrcid && formData.orcid.trim()) {
      const orcidToValidate = extractOrcidFromUrl(formData.orcid);
      if (!validateOrcid(orcidToValidate)) {
        setError("Please enter a valid ORCID ID (16 digits in XXXX-XXXX-XXXX-XXXX format).");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/signup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
            institution: formData.institution.trim(),
            email: formData.email.trim(),
            orcid: formatOrcid(orcidToValidate)
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Signup failed');
        }

        setSuccess(true);
      } catch (err) {
        setError(err.message || 'An error occurred during signup. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // No ORCID flow - search for author works
      try {
        setWorksLoading(true);
        const response = await fetch('/api/author-search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
            institution: formData.institution.trim()
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Author search failed');
        }

        if (data.authors && data.authors.length > 0) {
          setAuthorMatches(data.authors);
          setShowWorksSelection(true);
        } else {
          setError('No matching research profiles found. Please contact support for manual verification or try providing an ORCID ID.');
        }
      } catch (err) {
        setError(err.message || 'An error occurred while searching for your research profile. Please try again.');
      } finally {
        setLoading(false);
        setWorksLoading(false);
      }
    }
  };

  const handleWorkSelection = (work, isSelected) => {
    if (isSelected) {
      setSelectedWorks(prev => [...prev, work]);
    } else {
      setSelectedWorks(prev => prev.filter(w => w.title !== work.title || w.publication_year !== work.publication_year));
    }
  };

  const handleContinueWithWorks = async () => {
    if (selectedWorks.length === 0) {
      setError('Please select at least one work that you authored, or contact support for manual verification.');
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch('/api/confirm-authorship', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          selectedWorks,
          email: formData.email.trim(),
          name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
          institution: formData.institution.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Account creation failed');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'An error occurred while creating your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNoWorksMatch = () => {
    setError('No matching works found. Please contact our support team at support@lablock.ai for manual verification, or go back and try with an ORCID ID.');
  };

  const handleBackToForm = () => {
    setShowWorksSelection(false);
    setAuthorMatches([]);
    setSelectedWorks([]);
    setError("");
  };

  if (success) {
    return <AccountSetupPage />;
  }

  if (showWorksSelection) {
    return <WorksSelectionPage 
      authorMatches={authorMatches}
      selectedWorks={selectedWorks}
      onWorkSelection={handleWorkSelection}
      onContinue={handleContinueWithWorks}
      onNoWorksMatch={handleNoWorksMatch}
      onBack={handleBackToForm}
      loading={loading}
      error={error}
    />;
  }

  return (
    <div className="login-wrapper">
      <div className="login-container">
        <div className="login-logo-container">
          <img src={logo} alt="IPTalons Logo" className="login-logo" />
        </div>
        <h2 className="login-title">Join LabLock.ai</h2>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
          Create your researcher account to get started
        </p>
        
        <div className="login-form-container">
          <div className="login-form-card">
            <input
              id="firstName"
              name="firstName"
              type="text"
              className="login-input"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleInputChange}
              disabled={loading}
            />
            
            <input
              id="lastName"
              name="lastName"
              type="text"
              className="login-input"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleInputChange}
              disabled={loading}
            />
            
            <input
              id="institution"
              name="institution"
              type="text"
              className="login-input"
              placeholder="Institution Name"
              value={formData.institution}
              onChange={handleInputChange}
              disabled={loading}
            />
            
            <input
              id="email"
              name="email"
              type="email"
              className="login-input"
              placeholder="Work Email Address"
              value={formData.email}
              onChange={handleInputChange}
              disabled={loading}
            />
            
            {!noOrcid && (
              <>
                <input
                  id="orcid"
                  name="orcid"
                  type="text"
                  className="login-input"
                  placeholder="ORCID iD"
                  value={formData.orcid}
                  onChange={handleOrcidChange}
                  disabled={loading}
                />
                
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px', textAlign: 'left' }}>
                  Enter ORCID URL or ID number. <a href="https://orcid.org/register" target="_blank" rel="noopener noreferrer">Need an ORCID?</a>
                </div>
              </>
            )}
            
            <div style={{ marginTop: '15px', textAlign: 'left' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                fontSize: '14px', 
                color: '#666',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={noOrcid}
                  onChange={(e) => {
                    setNoOrcid(e.target.checked);
                    if (e.target.checked) {
                      setFormData(prev => ({ ...prev, orcid: "" }));
                    }
                    setError("");
                  }}
                  disabled={loading}
                  style={{ marginRight: '8px' }}
                />
                I don't have an ORCID ID
              </label>
              {noOrcid && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  marginTop: '5px',
                  fontStyle: 'italic'
                }}>
                  We'll help you identify your research profile using your publications.
                </div>
              )}
            </div>
          </div>
          
          {error && (
            <div style={{ 
              marginTop: '15px', 
              padding: '10px', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '4px',
              color: '#c33',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}
          
          <button 
            className="login-button" 
            onClick={handleSubmit}
            disabled={loading || worksLoading}
            style={{
              opacity: (loading || worksLoading) ? 0.7 : 1,
              cursor: (loading || worksLoading) ? 'not-allowed' : 'pointer'
            }}
          >
            {worksLoading ? 'Finding your research profile...' : 
             loading ? 'Creating Account...' : 
             'Create Account'}
          </button>
        </div>
      </div>
      <div className="login-footer">
        &copy; 2025 IPTalons, Inc. All rights reserved.
      </div>
    </div>
  );
};

const AccountSetupPage = () => {
  return (
    <div className="login-wrapper">
      <div className="login-container">
        <div className="login-logo-container">
          <img src={logo} alt="IPTalons Logo" className="login-logo" />
        </div>
        <h2 className="login-title">Account Setup In Progress</h2>
        
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ 
            fontSize: '48px', 
            color: '#1C6941', 
            marginBottom: '20px' 
          }}>
            ✓
          </div>
          
          <h3 style={{ color: '#1C6941', marginBottom: '20px' }}>
            Welcome to LabLock.ai!
          </h3>
          
          <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
            Your account has been successfully created and is now being set up. 
            We're verifying your research profile and configuring your institutional access.
          </p>
          
          <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '30px' }}>
            <strong>You'll receive an email within the next few minutes</strong> with your login credentials 
            and instructions on how to access your LabLock.ai dashboard.
          </p>
          
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #e9ecef', 
            borderRadius: '8px', 
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h4 style={{ color: '#1C6941', marginBottom: '15px' }}>
              What happens next?
            </h4>
            <ul style={{ 
              textAlign: 'left', 
              color: '#666', 
              paddingLeft: '20px',
              lineHeight: '1.8'
            }}>
              <li>We verify your ORCID and research profile</li>
              <li>Your institutional affiliations are confirmed</li>
              <li>Your personalized dashboard is prepared</li>
              <li>You receive your welcome email with login details</li>
            </ul>
          </div>
          
          <p style={{ 
            fontSize: '14px', 
            color: '#999',
            fontStyle: 'italic'
          }}>
            Questions? Contact our support team at support@lablock.ai
          </p>
        </div>
      </div>
      <div className="login-footer">
        &copy; 2025 IPTalons, Inc. All rights reserved.
      </div>
    </div>
  );
};

const WorksSelectionPage = ({ 
  authorMatches, 
  selectedWorks, 
  onWorkSelection, 
  onContinue, 
  onNoWorksMatch, 
  onBack, 
  loading, 
  error 
}) => {
  const isWorkSelected = (work) => {
    return selectedWorks.some(w => w.title === work.title && w.publication_year === work.publication_year);
  };

  return (
    <div className="login-wrapper">
      <div className="login-container">
        <div className="login-logo-container">
          <img src={logo} alt="IPTalons Logo" className="login-logo" />
        </div>
        <h2 className="login-title">Help us identify your research profile</h2>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px', lineHeight: '1.5' }}>
          Please select any works below that you authored. This helps us connect your account to your research profile.
        </p>
        
        <div className="login-form-container">
          <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
            {authorMatches.map((author, authorIndex) => (
              <div key={authorIndex} style={{ marginBottom: '30px' }}>
                <h3 style={{ 
                  color: '#1C6941', 
                  fontSize: '18px', 
                  marginBottom: '10px',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '5px'
                }}>
                  {author.display_name}
                </h3>
                {author.institutions.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    {author.institutions.map((institution, instIndex) => (
                      <div key={instIndex} style={{ 
                        fontSize: '13px', 
                        color: '#666', 
                        marginBottom: '4px',
                        paddingLeft: '8px',
                        borderLeft: '2px solid #e9ecef'
                      }}>
                        {institution}
                      </div>
                    ))}
                  </div>
                )}
                
                {author.works.map((work, workIndex) => (
                  <div key={workIndex} style={{
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '10px',
                    backgroundColor: isWorkSelected(work) ? '#f8f9fa' : 'white'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}>
                      <input
                        type="checkbox"
                        checked={isWorkSelected(work)}
                        onChange={(e) => onWorkSelection(work, e.target.checked)}
                        style={{ 
                          marginRight: '12px', 
                          marginTop: '2px',
                          transform: 'scale(1.1)'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: 'bold', 
                          color: '#333', 
                          marginBottom: '8px',
                          lineHeight: '1.4'
                        }}>
                          {work.title}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginBottom: '8px' 
                        }}>
                          {work.publication_year} • Cited by {work.cited_by_count} publications
                        </div>
                        {work.source_url && (
                          <a 
                            href={work.source_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                              fontSize: '12px', 
                              color: '#1C6941',
                              textDecoration: 'none'
                            }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            View Publication →
                          </a>
                        )}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            ))}
          </div>
          
          {error && (
            <div style={{ 
              marginBottom: '15px', 
              padding: '10px', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '4px',
              color: '#c33',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '10px' 
          }}>
            <button 
              className="login-button" 
              onClick={onContinue}
              disabled={loading || selectedWorks.length === 0}
              style={{
                opacity: (loading || selectedWorks.length === 0) ? 0.7 : 1,
                cursor: (loading || selectedWorks.length === 0) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Creating Account...' : `Continue with ${selectedWorks.length} selected work${selectedWorks.length !== 1 ? 's' : ''}`}
            </button>
            
            <button
              onClick={onNoWorksMatch}
              style={{
                background: 'none',
                border: 'none',
                color: '#1C6941',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline'
              }}
              disabled={loading}
            >
              None of these are my works
            </button>
            
            <button
              onClick={onBack}
              style={{
                background: 'none',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={loading}
            >
              ← Back to form
            </button>
          </div>
        </div>
      </div>
      <div className="login-footer">
        &copy; 2025 IPTalons, Inc. All rights reserved.
      </div>
    </div>
  );
};

export default LabLockSignupPage;